import * as React from 'react'
import { useState } from 'react'
import Popup from 'reactjs-popup'

type Template = {
  id: number
  name: string
  template_data?: any
}

type ManageTemplatesPopupProps = {
  open: boolean
  onClose: () => void
  templates: Template[]
  onUpdateTemplate: (templateId: number, newName: string) => void
  onDeleteTemplate: (templateId: number, templateName: string) => void
  isUpdating?: boolean
  isDeleting?: boolean
}

export default function ManageTemplatesPopup({
  open,
  onClose,
  templates,
  onUpdateTemplate,
  onDeleteTemplate,
  isUpdating,
  isDeleting,
}: ManageTemplatesPopupProps) {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const handleEditStart = (template: Template) => {
    setEditingId(template.id)
    setEditName(template.name)
  }

  const handleEditSave = (templateId: number) => {
    if (!editName.trim()) return
    onUpdateTemplate(templateId, editName.trim())
    setEditingId(null)
    setEditName('')
  }

  const handleEditCancel = () => {
    setEditingId(null)
    setEditName('')
  }

  const handleClose = () => {
    handleEditCancel()
    onClose()
  }

  return (
    <Popup
      open={open}
      onClose={handleClose}
      position="center center"
      contentStyle={{ width: 'auto', maxWidth: '100%', minWidth: '390px', zIndex: 9999 }}
      overlayStyle={{ zIndex: 9998 }}
      modal
      nested
      closeOnDocumentClick={false}
    >
      <div className="modal-popup">
        <a className="close" onClick={handleClose}>
          &times;
        </a>
        <div className="popup-header">
          <h6 className="modal-title">Manage Templates</h6>
        </div>
        <div className="popup-content">
          {templates.length === 0 ? (
            <p className="text-muted">No templates saved yet.</p>
          ) : (
            <div className="template-list">
              {templates.map((template: Template) => (
                <div key={template.id} className="template-item d-flex align-items-center mb-2 p-2 border rounded">
                  <div className="flex-grow-1 mr-2" style={{ minWidth: 0 }}>
                    {editingId === template.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') handleEditSave(template.id)
                          if (e.key === 'Escape') handleEditCancel()
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="text-truncate d-block" title={template.name}>
                        {template.name}
                      </span>
                    )}
                  </div>
                  <div className="template-actions flex-shrink-0">
                    {editingId === template.id ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success mr-1"
                          onClick={() => handleEditSave(template.id)}
                          disabled={!editName.trim() || isUpdating}
                        >
                          ✓
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleEditCancel}>
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary mr-1"
                          onClick={() => handleEditStart(template)}
                          title={`Edit ${template.name}`}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDeleteTemplate(template.id, template.name)}
                          disabled={isDeleting}
                          title={`Delete ${template.name}`}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="popup-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </Popup>
  )
}
